import { useState } from 'react';
import { App } from 'antd';
import { buildCsv, downloadCsv } from '../utils/csv';
import { getNowDownloadLabel } from '../utils/date';
import { getOhatScoreTotal } from '../utils/ohat-score-utils';
import { useTranslation } from 'react-i18next';
import { usePlanGuard } from './usePlanGuard';
import { patientFindAllOhatScores } from '../libs/ohat-api';
import { Patient } from '@models/patient';
import { OhatScore } from '@models/ohatScore';
import dayjs from 'dayjs';
import { Gender } from '../constants/gender';
import { PatientCareLevel } from '../constants/careLevel';
import { IndependentLifeLevel } from '../constants/independentLifeLevel';
import { DentalIntervention } from '../constants/dentalIntervention';

/**
 * 統合CSVの列順の定義（CSVはクライアント側生成に一本化しているため、ここが唯一の定義）。
 * 各列の見出しは `csv.columns.<id>` の翻訳を使う。
 */
const CSV_COLUMN_IDS = [
  'patientId',
  'name',
  'birthDate',
  'age',
  'gender',
  'careLevel',
  'independentLifeLevel',
  'dentalIntervention',
  'memo',
  'registeredAt',
  'evaluatedAt',
  'q1',
  'q2',
  'q3',
  'q4',
  'q5',
  'q6',
  'q7',
  'q8',
  'total',
];

/** 評価取得の同時リクエスト数の上限（管理者側CSVの並列化と同じ方針）。 */
const MAX_CONCURRENT = 3;

/** 評価列の空欄（評価0件の患者行に使う）。 */
const EMPTY_SCORE_CELLS = {
  evaluatedAt: '',
  q1: '',
  q2: '',
  q3: '',
  q4: '',
  q5: '',
  q6: '',
  q7: '',
  q8: '',
  total: '',
};

/**
 * 患者基本情報と評価履歴を統合したCSVのダウンロード処理（ライトプラン限定機能）。
 * 1行=1評価で全行に患者基本情報が付き、評価0件の患者も評価列が空欄の1行として出力する。
 * 対象患者は一覧に表示中の患者（filteredPatients）そのままで、アーカイブ済みを含むかは
 * 一覧の「アーカイブ表示」トグルに従う（見えている一覧＝出力内容にする）。
 * スコア本体は患者一覧APIに含まれないため、対象患者ごとに GET /patients/:id/ohat-scores を
 * 並列（同時 MAX_CONCURRENT 件まで）で取得する。1件でも失敗した場合はCSVを出力せず中断する。
 */
export function usePatientCsvDownload(filteredPatients: Patient[] = []): {
  download: () => Promise<void>;
  loading: boolean;
} {
  const { message } = App.useApp();
  const { t, i18n } = useTranslation();
  const { canUse, showUpgradeModal } = usePlanGuard();
  const [loading, setLoading] = useState(false);

  const download = async () => {
    // 非対応プランはダウンロードせずアップグレード誘導ダイアログを出す。
    if (!canUse('csv_download')) {
      showUpgradeModal();
      return;
    }

    // 出力対象は一覧に表示中の患者そのまま（アーカイブ表示ONならアーカイブ済みも含む）。
    // 患者は登録日の降順、同一患者内の評価は評価日の昇順。
    const sortedPatients = [...filteredPatients].sort(
      (a, b) =>
        dayjs(b.registeredAt).valueOf() - dayjs(a.registeredAt).valueOf(),
    );
    if (sortedPatients.length === 0) {
      message.warning(t('csv.empty'));
      return;
    }

    try {
      setLoading(true);

      // DOM更新などのために少し待つ
      await new Promise((resolve) => setTimeout(resolve, 100));

      // 患者一覧APIの ohatScores は evaluatedAt しか含まないため、スコア本体は患者ごとに取得する。
      // 1件でも失敗したら Promise.all が reject し、部分的なCSVを出さずに catch でエラー表示する。
      const scoresByPatientId = new Map<string, OhatScore[]>();
      for (let i = 0; i < sortedPatients.length; i += MAX_CONCURRENT) {
        const chunk = sortedPatients.slice(i, i + MAX_CONCURRENT);
        const responses = await Promise.all(
          chunk.map((p) => patientFindAllOhatScores(p.id)),
        );
        responses.forEach((res, idx) => {
          scoresByPatientId.set(chunk[idx].id, res?.ohatScores ?? []);
        });
      }

      const rows = sortedPatients.flatMap((p): Record<string, unknown>[] => {
        const patientCells = {
          patientId: p.id,
          name: p.name,
          birthDate: p.birthDate,
          age: p.age ?? '',
          gender: Gender.getByKey(p.gender)?.getLabel(i18n.language) ?? '',
          careLevel:
            PatientCareLevel.getByKey(p.careLevel)?.getLabel(i18n.language) ??
            '',
          independentLifeLevel:
            IndependentLifeLevel.getByKey(p.independentLifeLevel)?.getLabel(
              i18n.language,
            ) ?? '',
          dentalIntervention:
            DentalIntervention.getByKey(p.dentalIntervention)?.getLabel(
              i18n.language,
            ) ?? '',
          memo: p.memo ?? '',
          registeredAt: dayjs(p.registeredAt).format('YYYY-MM-DD HH:mm'),
        };

        // 評価の有無・内容はすべて取得結果を正とする（一覧APIの ohatScores は使わない）
        const scores = [...(scoresByPatientId.get(p.id) ?? [])].sort(
          (a, b) =>
            dayjs(a.evaluatedAt).valueOf() - dayjs(b.evaluatedAt).valueOf(),
        );
        if (scores.length === 0) {
          return [{ ...patientCells, ...EMPTY_SCORE_CELLS }];
        }

        return scores.map((s) => ({
          ...patientCells,
          evaluatedAt: dayjs(s.evaluatedAt).format('YYYY-MM-DD HH:mm'),
          q1: s.q1,
          q2: s.q2,
          q3: s.q3,
          q4: s.q4,
          q5: s.q5,
          q6: s.q6,
          q7: s.q7,
          q8: s.q8,
          total: getOhatScoreTotal(s),
        }));
      });

      // 見出しはダウンロード時点の表示言語に合わせる。
      const columns = CSV_COLUMN_IDS.map((id) => ({
        id,
        displayName: t(`csv.columns.${id}`),
      }));

      const csvStr = buildCsv(columns, rows);
      downloadCsv(csvStr, `patients_${getNowDownloadLabel()}.csv`);
    } catch (e) {
      console.error('CSV download error:', e);
      message.error(t('csv.failed'));
    } finally {
      setLoading(false);
    }
  };

  return { download, loading };
}
